import React, { type JSX } from 'react';
import EnhancedMarkdown from '../EnhancedMarkdown/EnhancedMarkdown';
import {
  IconDefaultsContextProvider,
  iconSizeExtraLarge,
} from '../../contexts/useIconDefaults';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import classNames from 'classnames';
import buildsEnhancedMarkdownOverrides from '@resources/js/helpers/buildsEnhancedMarkdownOverrides';
import WarningParagraph from '@resources/js/components/WarningParagraph/WarningParagraph';

export type BuildsBestConsumablesViewModel = {
  title: string;
  description: string;
  warning: string | null;
  itemLabel: string;
  popularityLabel: string;
  itemBreakdowns: BuildsItemBreakdownViewModel[];
};

export type BuildsItemBreakdownViewModel = {
  slotLabel: string;
  bestLabel: string;
  itemMarkdown: string;
  iconMarkdown: string;
  popularityMarkdown: string;
  sideAffinity?: 'left' | 'right';
};

type BestConsumableItemProps = BuildsItemBreakdownViewModel & {
  key: number;
  side: 'left' | 'right';
};

const enhancedMarkdownOptions = {
  lazyLoad: false,
};

const iconDefaults = {
  icon: {
    size: iconSizeExtraLarge,
    rounded: false,
    bordered: true,
  },
  item: { size: 20 },
  ability: { size: 20, rounded: false, bordered: true },
};

function BestConsumableItem({
  itemMarkdown,
  popularityMarkdown,
  side,
}: BestConsumableItemProps): JSX.Element {
  return (
    <div className={`best-consumable-item best-consumable-item--${side}`}>
      <div className={'best-consumable-item__name'}>
        <EnhancedMarkdown markdown={itemMarkdown} wrapped={false} />
      </div>
      <div className={'best-consumable-item__popularity'}>
        <EnhancedMarkdown markdown={popularityMarkdown} wrapped={false} />
      </div>
    </div>
  );
}

export default function BuildsBestConsumablesSection({
  title,
  description,
  warning,
  itemLabel,
  popularityLabel,
  itemBreakdowns,
}: BuildsBestConsumablesViewModel): JSX.Element {
  const leftTrack = itemBreakdowns.filter(
    (breakdown) => breakdown.sideAffinity === 'left',
  );
  const rightTrack = itemBreakdowns.filter(
    (breakdown) => breakdown.sideAffinity === 'right',
  );

  const unknown = itemBreakdowns.filter((breakdown) => !breakdown.sideAffinity);

  unknown.forEach((breakdown, index) => {
    const track = index % 2 === 0 ? leftTrack : rightTrack;
    track.push(breakdown);
  });

  return (
    <EnhancedMarkdownOptionsContextProvider value={enhancedMarkdownOptions}>
      <div className='builds-best-consumables-section'>
        {warning && (
          <WarningParagraph>
            <EnhancedMarkdown markdown={warning} wrapped={false} />
          </WarningParagraph>
        )}
        <EnhancedMarkdown
          markdown={`
# ${title}

${description}
`}
          overrides={buildsEnhancedMarkdownOverrides}
        />
        {itemBreakdowns.length > 0 ? (
          <IconDefaultsContextProvider overrides={iconDefaults}>
            <div className='builds-best-consumables-section__columns'>
              {leftTrack.length > 0 ? (
                <div className='builds-best-consumables-section__column'>
                  <div className='builds-best-consumables-section__header'>
                    <div
                      className={
                        'builds-best-consumables-section__header-column'
                      }
                    >
                      {itemLabel}
                    </div>
                    <div
                      className={
                        'builds-best-consumables-section__header-column'
                      }
                    >
                      {popularityLabel}
                    </div>
                  </div>
                  <div className='builds-best-consumables-section__group'>
                    {leftTrack.map((itemBreakdown, index) => {
                      return (
                        <div
                          key={index}
                          className={'builds-best-consumables-section__item'}
                        >
                          <BestConsumableItem
                            key={index}
                            {...itemBreakdown}
                            side={'left'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {rightTrack.length > 0 ? (
                <div className='builds-best-consumables-section__column'>
                  <div className='builds-best-consumables-section__header'>
                    <div
                      className={
                        'builds-best-consumables-section__header-column'
                      }
                    >
                      {popularityLabel}
                    </div>
                    <div
                      className={
                        'builds-best-consumables-section__header-column'
                      }
                    >
                      {itemLabel}
                    </div>
                  </div>
                  <div className='builds-best-consumables-section__group'>
                    {rightTrack.map((itemBreakdown, index) => {
                      return (
                        <div
                          key={index}
                          className={classNames(
                            'builds-best-consumables-section__item',
                          )}
                        >
                          <BestConsumableItem
                            key={index}
                            {...itemBreakdown}
                            side={'right'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </IconDefaultsContextProvider>
        ) : (
          <BuildsNoDataPlaceholder marginTop={1} />
        )}
      </div>
    </EnhancedMarkdownOptionsContextProvider>
  );
}
